import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { flocknoteKeys } from "Flocknote/API/keys";

import { twoFALoginKeys } from "Login/TwoFA/API/keys";
import pickBy from "lodash/pickBy";

import { useMemberToobarContext } from "../MemberToolbarState";

import {
  contactUs,
  getOtherNetworks,
  logout,
  prayerRequest,
  setLanguage,
} from "./fetch";
import memberToolbarKeys from "./keys";

const useSetLanguage = () => {
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState("");

  return {
    ...useMutation({
      mutationKey: memberToolbarKeys.setLanguage(),
      mutationFn: async (language) => {
        setErrorMessage("");
        const newUserData = await setLanguage({ language });
        const queryKey = flocknoteKeys.user();
        queryClient.setQueryData(queryKey, newUserData);
      },
      onSuccess: () => {
        setErrorMessage("");
      },
      onError: (err) => {
        if (typeof err === "string") setErrorMessage(err);
        if (typeof err?.message === "string") setErrorMessage(err.message);
        else
          setErrorMessage("Something went wrong with setting that language.");
      },
    }),
    errorMessage,
    setErrorMessage,
  };
};

const useContactUs = ({
  givingInquiry,
  generalInquiry,
  flocknoteSupport,
  message,
  signupListID,
  noteID,
}) => {
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);

  return {
    ...useMutation({
      mutationKey: memberToolbarKeys.contactUs(),
      mutationFn: async () => {
        setErrorMessage("");
        return await contactUs(
          pickBy({
            message,
            givingInquiry,
            generalInquiry,
            flocknoteSupport,
            signupListID,
            noteID,
          })
        );
      },
      onError: (err) => {
        setShowError(true);
        if (typeof err?.message === "string")
          return setErrorMessage(err.message);
        if (typeof err === "string") return setErrorMessage(err);
        setErrorMessage(
          "Something went wrong with sending that message. Please try again or contact help@flocknote.com."
        );
      },
    }),
    errorMessage,
    showError,
    setShowError,
  };
};

const usePrayerRequest = () => {
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const { message } = useMemberToobarContext();

  return {
    ...useMutation({
      mutationKey: memberToolbarKeys.prayerRequest(),
      mutationFn: async () => {
        setErrorMessage("");
        return await prayerRequest({ message });
      },
      onError: (err) => {
        setShowError(true);
        if (typeof err?.message === "string")
          return setErrorMessage(err.message);
        if (typeof err === "string") return setErrorMessage(err);
        setErrorMessage(
          "Something went wrong with that prayer request. Please try again or contact help@flocknote.com."
        );
      },
    }),
    errorMessage,
    showError,
    setShowError,
  };
};

const useGetOtherNetworks = () => {
  return useQuery({
    queryKey: memberToolbarKeys.getOtherNetworks(),
    queryFn: () => getOtherNetworks(),
  });
};

const useLogout = ({ invalidate = true } = {}) => {
  const queryClient = useQueryClient();

  return {
    ...useMutation({
      mutationKey: memberToolbarKeys.logout(),
      mutationFn: async () => {
        return await new Promise((res) => {
          logout().then(res).catch(res);
        });
      },
      onSuccess: () => {
        if (invalidate) {
          queryClient.setQueryData(flocknoteKeys.user(), {
            authorized: false,
            success: true,
            userID: null,
          });
          queryClient.invalidateQueries(flocknoteKeys.user());
          queryClient.removeQueries(twoFALoginKeys.all());
          queryClient.invalidateQueries(twoFALoginKeys.all());
        }
      },
    }),
  };
};

export {
  useSetLanguage,
  useGetOtherNetworks,
  useLogout,
  useContactUs,
  usePrayerRequest,
};
